import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCreateDistrict } from "@/api/reseller/mutations";
import { useCreatedDistrictManagers } from "@/api/reseller/queries";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { EmptyCell } from "@/components/ui/empty-cell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { createDistrictSchema } from "@/lib/schemas/district.schema";
import { getDrfFieldErrors } from "@/utils/errors";

type CreateDistrictDialogProps = {
	open: boolean;
	onClose: () => void;
};

export function CreateDistrictDialog({
	open,
	onClose,
}: CreateDistrictDialogProps) {
	const { t } = useTranslation();
	const [name, setName] = useState("");
	const [managerId, setManagerId] = useState<string>("");
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

	const create = useCreateDistrict();
	const { data: managersData, isLoading: managersLoading } =
		useCreatedDistrictManagers({ page_size: 100 });

	const managers = managersData?.results ?? [];

	function handleClose() {
		setName("");
		setManagerId("");
		setFieldErrors({});
		onClose();
	}

	function handleSubmit() {
		setFieldErrors({});
		const result = createDistrictSchema.safeParse({ name, managerId });
		if (!result.success) {
			toast.error(
				result.error.issues[0]?.message ?? t("common.validationError"),
			);
			return;
		}
		create.mutate(
			{ name: name.trim(), manager: Number(managerId) },
			{
				onSuccess: () => {
					toast.success(t("districts.created"));
					handleClose();
				},
				onError: (error) => {
					if (error.message === "rate_limited") return;
					setFieldErrors(getDrfFieldErrors(error));
					toast.error(t("districts.createError"));
				},
			},
		);
	}

	const canSubmit =
		name.trim() && managerId && !create.isPending && !create.isRateLimited;

	return (
		<Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Utwórz osiedle</DialogTitle>
					<DialogDescription>Podaj dane nowego osiedla</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-4 py-2">
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="district-name">Nazwa osiedla</Label>
						<Input
							id="district-name"
							name="district-name"
							autoComplete="off"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="np. Osiedle Słoneczne"
							onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
							aria-invalid={!!fieldErrors.name}
							aria-describedby={
								fieldErrors.name ? "district-name-error" : undefined
							}
						/>
						{fieldErrors.name && (
							<p
								id="district-name-error"
								className="text-destructive text-sm"
								role="alert"
							>
								{fieldErrors.name}
							</p>
						)}
					</div>
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="district-manager">Zarządca</Label>
						<Select
							value={managerId}
							onValueChange={setManagerId}
							disabled={managersLoading}
						>
							<SelectTrigger
								className="w-full"
								aria-invalid={!!fieldErrors.manager}
							>
								<SelectValue
									placeholder={
										managersLoading ? "Ładowanie…" : "Wybierz zarządcę"
									}
								/>
							</SelectTrigger>
							<SelectContent>
								{managers.map((m) => (
									<SelectItem key={m.id} value={String(m.id)}>
										{m.company_name || m.emails?.[0] || <EmptyCell />}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						{fieldErrors.manager && (
							<p className="text-destructive text-sm" role="alert">
								{fieldErrors.manager}
							</p>
						)}
					</div>
				</div>

				<DialogFooter>
					<Button
						size="sm"
						variant="outline"
						onClick={handleClose}
						disabled={create.isPending}
					>
						Anuluj
					</Button>
					<Button onClick={handleSubmit} disabled={!canSubmit}>
						{create.isPending ? "Tworzenie…" : "Utwórz"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
